import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import classNames from 'classnames';

import Pen from '@ast/magma/components/icon/icons/Pen';

import { Alert } from '@ast/magma/components/alert';
import { Button } from '@ast/magma/components/button';

import { FieldBehaviorMode } from '@app/queryTyping';

import { PortalDialogForm } from '@app/common/components/configurable-wizards/PortalDialogForm';
import { EmailFieldFragment } from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import { FieldComponent, FieldLocator } from '@app/common/configurable-wizards/types';
import { combineNames, isEmailField } from '@app/common/configurable-wizards/utils';

import styles from './EmailField.pcss';
import { EmailInput } from './EmailInput';

export const EmailFieldComponent: FieldComponent<EmailFieldFragment> = ({
  parent,
  field,
  fieldProps,
}) => {
  const editable = field.fieldBehaviorMode !== FieldBehaviorMode.READONLY;
  const name = combineNames(parent, field.id);

  const {
    setValue,
    watch,
    register,
  } = useFormContext();
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [email, setEmail] = useState(field.sValue || '');
  const emailAtField = watch(name);

  useEffect(() => {
    setValue(name, email);
  }, [setValue, email]);

  const renderEmailInput = () => (
    <EmailInput
      name={name}
      field={
          field.sValue
            ? {
              ...field,
              // if email exists at the core then restrict its deleting at the UI
              fieldBehaviorMode: FieldBehaviorMode.REQUIRED,
            } : field
        }
      {...fieldProps}
    />
  );

  const handleDialogFormSubmit = useCallback(
    (data) => {
      setEditMode(false);
      setEmail(data[name]);
    },
    [],
  );

  return (
    <div>
      {field.sValue ? (
        <>
          {/* -------- Email exist in the core -------- */}
          <div
            className={classNames(
              styles.emailLabel,
              editable && styles.emailLabelEditable,
            )}
          >
            {field.label}
          </div>
          <div>
            <span
              aria-label={t(
                'email-field.current-value.aria-label|Aria label text for the current email value',
                'Your current email is: {{email}}',
                { email },
              )}
              // make it focusable to make able disabilities-people recognise their email
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              data-stable-name={`${field.id}CurrentValueLabel`}
            >
              {email}
            </span>

            {editable && (
            <Button
              look="icon"
              onClick={() => setEditMode(true)}
              icon={<Pen data-stable-name="EditButtonPen" />}
              aria-label={t('email-field.edit-email-button.aria-label|Aria label for the edit email button', 'Edit email')}
              data-stable-name={`${field.id}EditEmailButton`}
            />
            )}
          </div>

          {editMode && (
            <PortalDialogForm
              formOptions={{
                defaultValues: {
                  // get current email from the field, otherwise use state
                  [name]: typeof emailAtField !== 'undefined' ? emailAtField : email,
                },
              }}
              dialogOptions={{
                title: t('email-field.edit-email-dialog.title|Title for the email editing dialog', 'Edit email address') || '',
                showClose: {
                  // eslint-disable-next-line i18next/no-literal-string
                  'aria-label': t('email-field.edit-email-dialog.close-button.aria-label|Aria label for the close button in the email editing dialog', 'Close email editing'),
                },
                onClose: () => setEditMode(false),
              }}
              onSubmit={handleDialogFormSubmit}
              data-stable-name={`${field.id}EditEmailDialog`}
            >
              {renderEmailInput()}
            </PortalDialogForm>
          )}

          {/* Hidden input to keep the value during form submiting */}
          <input
            type="hidden"
            defaultValue={email}
            data-stable-name={`${field.id}Hidden`}
            {...register(name)}
          />
        </>
      ) : (
        <>
          {/* -------- Email absent in the core -------- */}
          {editable
            ? renderEmailInput()
            : (
              <Alert
                level="info"
                aria-live="off"
                data-stable-name={`${field.id}EmailAbsentNotification`}
              >
                {t(
                  'email-field.readonly-and-absent-msg|The explanation message when email absent at the Core and user cannot input it',
                  'Sorry, we don\'t have your email in our records. Please add it in Contact Information under the Settings Menu, or contact the nearest branch.',
                )}
              </Alert>
            )}
        </>
      )}
    </div>
  );
};

export const EmailFieldLocator: FieldLocator = ({ field }) => (
  isEmailField(field)
    ? EmailFieldComponent
    : undefined
);
